<template>
  <div class="page-container">
    <h2 class="page-title">自提门店管理</h2>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-input
        v-model="searchKey"
        placeholder="请输入门店名称或地址"
        style="width: 300px"
        clearable
        @keyup.enter="handleSearch"
      />
      <el-button type="primary" @click="handleSearch">查询</el-button>
      <el-button @click="resetSearch">重置</el-button>
      <el-button type="success" @click="openAddDialog">新增门店</el-button>
    </div>

    <!-- 数据表格 -->
    <el-table :data="storeList" border v-loading="loading">
      <el-table-column prop="id" label="门店ID" width="100" />
      <el-table-column prop="name" label="门店名称" width="180" />
      <el-table-column prop="address" label="门店地址" />
      <el-table-column prop="poi_id" label="门店POI" />
      <el-table-column label="营业时间" width="220">
        <template #default="{row}">
          {{ row.start_time }} - {{ row.end_time }}
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100">
        <template #default="{row}">
          <el-tag :type="row.status === 1 ? 'success' : 'danger'">
            {{ row.status === 1 ? '营业中' : '已关闭' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="{ row }">
          <el-button type="primary" size="small" @click="openEditDialog(row)">编辑</el-button>
          <el-popconfirm title="确定删除该门店？" @confirm="handleDelete(row.id)">
            <template #reference>
              <el-button type="danger" size="small">删除</el-button>
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.limit"
        :total="pagination.total"
        :page-sizes="[10, 20, 50]"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="fetchData"
        @current-change="fetchData"
      />
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog v-model="dialogVisible" :title="isEditMode ? '编辑门店' : '新增门店'" width="600px">
      <el-form :model="formData" label-width="100px">
        <el-form-item label="门店名称" prop="name" required>
          <el-input v-model="formData.name" />
        </el-form-item>
        <el-form-item label="门店地址" prop="address" required>
          <el-input v-model="formData.address" />
        </el-form-item>
        <el-form-item label="门店POI" prop="poi_id">
          <el-input v-model="formData.poi_id" />
        </el-form-item>
        <el-form-item label="营业时间">
          <el-time-picker
            v-model="businessHours"
            is-range
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="HH:mm:ss"
          />
        </el-form-item>
        <el-form-item label="营业状态">
          <el-radio-group v-model="formData.status">
            <el-radio :label="1">营业中</el-radio>
            <el-radio :label="-1">已关闭</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage } from 'element-plus'
import Cookies from 'js-cookie'
import config from '@/config'
import axios from 'axios'

const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
const storeId = userInfo.id // 注意：这里获取的是用户ID，实际应传递商户ID（需根据业务调整）

// 数据相关
const loading = ref(false)
const searchKey = ref('')
const dialogVisible = ref(false)
const isEditMode = ref(false)
const storeList = ref([])
const formData = reactive({
  id: null,
  store_id: storeId,
  name: '',
  address: '',
  poi_id: '',
  start_time: '10:00:00',
  end_time: '22:00:00',
  status: 1
})

// 营业时间选择器绑定
const businessHours = computed({
  get: () => [formData.start_time, formData.end_time],
  set: (val) => {
    [formData.start_time, formData.end_time] = val || ['10:00:00', '22:00:00']
  }
})

// 分页配置
const pagination = reactive({
  page: 1,
  limit: 10,
  total: 0
})

// 加载数据
const fetchData = async () => {
  try {
    loading.value = true
    const params = {
      store_id: storeId,
      page: pagination.page,
      limit: pagination.limit,
      search: searchKey.value
    }

    const { data } = await axios.get(`${config.BASE_URL}/shop/api/ztstore_api.php`, { params })
    
    if (data.code === 200) {
      storeList.value = data.data.list
      pagination.total = data.data.pagination.total
    }
  } catch (error) {
    ElMessage.error('数据加载失败')
  } finally {
    loading.value = false
  }
}

// 搜索处理
const handleSearch = () => {
  pagination.page = 1
  fetchData()
}

// 重置搜索
const resetSearch = () => {
  searchKey.value = ''
  fetchData()
}

// 打开弹窗
const openAddDialog = () => {
  isEditMode.value = false
  Object.assign(formData, {
    id: null,
    name: '',
    address: '',
    poi_id: '',
    start_time: '10:00:00',
    end_time: '22:00:00',
    status: 1
  })
  dialogVisible.value = true
}

// 打开编辑
const openEditDialog = (row) => {
  isEditMode.value = true
  Object.assign(formData, {
    id: row.id,
    name: row.name,
    address: row.address,
    poi_id: row.poi_id,
    start_time: row.start_time,
    end_time: row.end_time,
    status: row.status
  })
  dialogVisible.value = true
}

// 提交表单
const submitForm = async () => {
  try {
    const url = `${config.BASE_URL}/shop/api/ztstore_api.php`
    const method = isEditMode.value ? 'put' : 'post'
    
    await axios[method](url, formData)
    ElMessage.success('操作成功')
    dialogVisible.value = false
    fetchData()
  } catch (error) {
    ElMessage.error('操作失败')
  }
}

// 删除处理
const handleDelete = async (id) => {
  try {
    await axios.delete(`${config.BASE_URL}/shop/api/ztstore_api.php`, {
      data: { id, store_id: storeId }
    })
    ElMessage.success('删除成功')
    fetchData()
  } catch (error) {
    ElMessage.error('删除失败')
  }
}

// 初始化加载
onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.page-title {
  margin-bottom: 20px;
  color: #333;
}

.search-bar {
  margin-bottom: 20px;
  display: flex;
  gap: 10px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>